import React, { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    Button, Text, VStack, HStack, Input, Alert, AlertIcon, Box, Center, Spinner,
    FormControl, FormLabel, InputGroup, InputLeftAddon, useToast, Divider, Badge,
} from '@chakra-ui/react';
import { FaMoneyBillWave } from 'react-icons/fa';
import { getReembolsoMpInfo, executarReembolsoMp } from '../../services/api';
import { formatBRL } from '../../utils/formatters';

const TEXTO_CONFIRMACAO = 'TENHO CERTEZA';

const LinhaResumo = ({ label, children }) => (
    <HStack justify="space-between" fontSize="sm">
        <Text color="gray.600">{label}</Text>
        <Box fontWeight="bold" textAlign="right">{children}</Box>
    </HStack>
);

// recurso = de onde parte o estorno: 'devolucoes_kanban' ou 'reclamacoes'.
const ReembolsoMercadoPagoModal = ({ isOpen, onClose, registroId, recurso = 'devolucoes_kanban', onSuccess }) => {
    const toast = useToast();
    const [info, setInfo] = useState(null);
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [valor, setValor] = useState('');
    const [confirmacao, setConfirmacao] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setInfo(null);
        setValor('');
        setConfirmacao('');
        setLoadingInfo(true);
        getReembolsoMpInfo(registroId, recurso).then((data) => {
            setInfo(data);
            if (data?.success) setValor(String(data.valor_sugerido ?? ''));
            setLoadingInfo(false);
        });
    }, [isOpen, registroId, recurso]);

    const rotulo = recurso === 'reclamacoes' ? 'Esta reclamação' : recurso === 'vendas/ocorrencias' ? 'Este item' : 'Esta devolução';
    const valorNum = Number(valor);
    const valorValido = !Number.isNaN(valorNum) && valorNum > 0 && info?.success && valorNum <= (info.valor_disponivel + 0.001);
    const jaReembolsadoPorAqui = !!info?.reembolso_registrado;
    const podeConfirmar = info?.success && valorValido && confirmacao === TEXTO_CONFIRMACAO && !jaReembolsadoPorAqui && !enviando;

    const handleConfirmar = async () => {
        setEnviando(true);
        try {
            const resp = await executarReembolsoMp(registroId, { valor: valorNum, confirmacao }, recurso);
            if (resp?.success) {
                toast({ title: resp.message || 'Reembolso enviado ao Mercado Pago.', status: 'success', duration: 8000, isClosable: true });
                onSuccess && onSuccess();
                onClose();
            } else {
                toast({ title: 'Reembolso não realizado', description: resp?.error || 'Erro desconhecido.', status: 'error', duration: 10000, isClosable: true });
            }
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={enviando ? () => {} : onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent mx={4}>
                <ModalHeader>
                    <HStack>
                        <FaMoneyBillWave color="green" />
                        <Text>Devolver dinheiro ao cliente (Mercado Pago)</Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton isDisabled={enviando} />
                <ModalBody>
                    {loadingInfo && (
                        <Center py={10}>
                            <VStack>
                                <Spinner size="lg" color="green.500" />
                                <Text fontSize="sm" color="gray.500">Consultando o pagamento no Mercado Pago...</Text>
                            </VStack>
                        </Center>
                    )}

                    {!loadingInfo && info && !info.success && (
                        <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="sm">{info.error || 'Não foi possível consultar o pagamento.'}</Text>
                        </Alert>
                    )}

                    {!loadingInfo && info?.success && (
                        <VStack align="stretch" spacing={3}>
                            <Box bg="gray.50" p={3} borderRadius="md" borderWidth="1px">
                                <VStack align="stretch" spacing={1}>
                                    <LinhaResumo label="Pedido">{info.order_number}</LinhaResumo>
                                    <LinhaResumo label="Cliente">{info.cliente || 'N/I'}</LinhaResumo>
                                    <LinhaResumo label="Pagamento MP">
                                        {info.mp_payment_id} <Badge colorScheme="blue">{info.metodo}</Badge>
                                    </LinhaResumo>
                                    <LinhaResumo label="Status no MP">
                                        <Badge colorScheme={info.status_pagamento === 'approved' ? 'green' : 'orange'}>
                                            {info.status_pagamento}
                                        </Badge>
                                    </LinhaResumo>
                                    <Divider my={1} />
                                    <LinhaResumo label="Valor pago">{formatBRL(info.valor_pago)}</LinhaResumo>
                                    <LinhaResumo label="Já reembolsado no MP">{formatBRL(info.valor_ja_reembolsado)}</LinhaResumo>
                                    <LinhaResumo label="Disponível para reembolso">{formatBRL(info.valor_disponivel)}</LinhaResumo>
                                </VStack>
                            </Box>

                            {jaReembolsadoPorAqui && (
                                <Alert status="error" borderRadius="md">
                                    <AlertIcon />
                                    <Text fontSize="sm">
                                        {rotulo} já teve reembolso de {formatBRL(info.reembolso_registrado.valor)} via
                                        Mercado Pago em {info.reembolso_registrado.data} por {info.reembolso_registrado.user_name}.
                                    </Text>
                                </Alert>
                            )}

                            {!jaReembolsadoPorAqui && info.status_money === 'refunded' && (
                                <Alert status="warning" borderRadius="md">
                                    <AlertIcon />
                                    <Text fontSize="sm">
                                        {rotulo} já consta como <b>reembolsada</b> no sistema. Confirme que o dinheiro
                                        ainda não foi devolvido por outro meio (PIX manual, por exemplo) antes de prosseguir.
                                    </Text>
                                </Alert>
                            )}

                            {!jaReembolsadoPorAqui && (
                                <>
                                    <FormControl>
                                        <FormLabel fontSize="sm" mb={1}>Valor a devolver</FormLabel>
                                        <InputGroup size="sm">
                                            <InputLeftAddon>R$</InputLeftAddon>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                max={info.valor_disponivel}
                                                value={valor}
                                                onChange={(e) => setValor(e.target.value)}
                                                isDisabled={enviando}
                                            />
                                        </InputGroup>
                                        {!valorValido && valor !== '' && (
                                            <Text fontSize="xs" color="red.500" mt={1}>
                                                Informe um valor entre R$ 0,01 e {formatBRL(info.valor_disponivel)}.
                                            </Text>
                                        )}
                                    </FormControl>

                                    <Alert status="warning" borderRadius="md" alignItems="flex-start">
                                        <AlertIcon />
                                        <Text fontSize="sm">
                                            O valor será estornado ao cliente <b>direto no Mercado Pago</b> e{' '}
                                            <b>não pode ser desfeito</b>. Para autorizar, digite{' '}
                                            <b>{TEXTO_CONFIRMACAO}</b> no campo abaixo.
                                        </Text>
                                    </Alert>

                                    <Input
                                        placeholder={`Digite ${TEXTO_CONFIRMACAO}`}
                                        value={confirmacao}
                                        onChange={(e) => setConfirmacao(e.target.value)}
                                        isDisabled={enviando}
                                        autoComplete="off"
                                    />
                                </>
                            )}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose} isDisabled={enviando}>
                        Cancelar
                    </Button>
                    <Button
                        colorScheme="red"
                        leftIcon={<FaMoneyBillWave />}
                        onClick={handleConfirmar}
                        isDisabled={!podeConfirmar}
                        isLoading={enviando}
                        loadingText="Reembolsando..."
                    >
                        Devolver {valorValido ? formatBRL(valorNum) : 'dinheiro'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ReembolsoMercadoPagoModal;
